import { axiosConfig } from '@api';
import axios, { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react'

export const OPTIN_TYPES = [
  "SHARE_BACK_FIFA",
  "SHARE_IAP_FIFA",
  "PANINI_PROFILING",
  "PANINI_MARKETING",
  "CONTEST",
  "SHARE_PANINI_BR",
  "SHARE_PANINI_NA",
] as const;

export type OptinUID = (typeof OPTIN_TYPES)[number]

// Late change request be Panini. Keep in sync with Unity implementation:
// See "Assets/Features/TermsAndConditions/Scripts/Models/OptinsStatusInfo.cs"
const HIDDEN_DURING_ONBOARDING: OptinUID[] = ['SHARE_IAP_FIFA'];

const OPTINS_WITH_RADIOBUTTONS: OptinUID[]= ['SHARE_PANINI_BR', 'SHARE_PANINI_NA'];

export interface OptinInfo {
  uid: OptinUID,
  explicit: boolean,
  mandatory: boolean,
  accepted?: boolean
}

interface IOptinContext {
  needsToShowOptinsScreen: boolean,
  answeredInfos: OptinAnsweredInfo[],
  answerOptin: (uid: OptinUID, accepted: boolean) => void,
  markOptinsScreenToNotBeShown: () => void,
}

const OptinsContext = React.createContext<IOptinContext>({needsToShowOptinsScreen: false, answeredInfos: [], markOptinsScreenToNotBeShown: () => {}, answerOptin: () => {}})

type ChoiceType = "checkbox" | "radio"

export const choiceType = (uid: OptinUID): ChoiceType => OPTINS_WITH_RADIOBUTTONS.indexOf(uid) > -1 ? 'radio' : 'checkbox'

export type OptinAnsweredInfo = ReturnType<typeof buildOptinsAnsweredInfo>[number]

function isOptinForOnboarding(optin: OptinInfo): boolean {
  return optin.explicit && HIDDEN_DURING_ONBOARDING.indexOf(optin.uid) === -1;
}

const checkIfNeedsToShowOptinsScreenInitially = (optins: OptinInfo[]): boolean => {
  return !!optins.find((o) => isOptinForOnboarding(o) && (typeof(o.accepted) === 'undefined'))
    || !!optins.find((o) => o.mandatory && !o.accepted);
}

export const buildOptinsAnsweredInfo = (optins: OptinInfo[]) => {
  return optins.filter(isOptinForOnboarding).map((o) => {
    return {
      uid: o.uid,
      accepted: !!o.accepted,
      choiceType: choiceType(o.uid),
      required: o.mandatory,
      madeAChoice: choiceType(o.uid) === 'checkbox' || typeof(o.accepted) !== 'undefined' // checkboxes are seen as "auto-chosen"
    }
  })
}

export const answerOptins = (optins: {uid: OptinUID, accepted: boolean}[]) => {
  return axios.post<{}, AxiosResponse<{success: boolean, error?: string}>>("/mobile/optins/answer", { optins }, axiosConfig)
}

export const answerAnOptin = (infos: OptinAnsweredInfo[], uid: OptinUID, accepted: boolean) => infos.map((i) => {
  if (i.uid !== uid) { return i}
  return {
    ...i,
    madeAChoice: true,
    accepted,
  }
})

export const useFetchOptinsInfo = (): { initialzed: boolean, contextValue?: IOptinContext } => {
  const [state, setState] = useState<ReturnType<typeof useFetchOptinsInfo>>({ initialzed: false });

  useEffect(() => { // request optins status
    let isActive = true
    axios.get<{}, AxiosResponse<{optins: OptinInfo[]}>>("/mobile/optins/status", axiosConfig).then((response) => {
      if (isActive) { setState({
        initialzed: true,
        contextValue: {
          needsToShowOptinsScreen: checkIfNeedsToShowOptinsScreenInitially(response.data.optins),
          answeredInfos: buildOptinsAnsweredInfo(response.data.optins),
          answerOptin(uid, accepted) {
            setState((prev) => {
              return {
                ...prev,
                contextValue: {
                  ...prev.contextValue!,
                  answeredInfos: answerAnOptin(prev.contextValue?.answeredInfos!, uid, accepted),
                }
              }
            })
          },
          markOptinsScreenToNotBeShown: () => setState(
            (prev) => (
              {...prev,
                contextValue: {
                  ...prev.contextValue!,
                  needsToShowOptinsScreen: false,
                }
              }))
            }
      }) }
    }).catch((err: Error) => {
      console.warn(err)
    })

    return () => { // cleanup
      isActive = false
    }
  }, [])

  return state;
}

export default OptinsContext;
